import type React from 'react';
import { useEffect, useRef } from 'react';
import QrWorker from '@/assets/workers/qr.worker?worker';

export type BarcodeScannerProps = {
  onScan: (data: string) => void;
};

const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: 'environment',
    width: { ideal: 640 },
    height: { ideal: 480 },
    frameRate: { ideal: 15 },
  },
};

const SCAN_DELAY = 1000;

export const useBarcodeScanner = (
  videoRef: React.RefObject<HTMLVideoElement>,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  onScan: (data: string) => void,
) => {
  const qrWorker = useRef<Worker>();

  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream;
    let requestId: number;
    let lastScanTime = 0;

    qrWorker.current = new QrWorker();

    qrWorker.current.onmessage = (e) => {
      const code = e.data;
      if (code?.data) {
        onScan(code.data);
        cancelAnimationFrame(requestId);
      }
    };

    const tick = (currentTime: number) => {
      const canvas = canvasRef.current;
      const video = videoRef.current;

      if (canvas && video && video.readyState === video.HAVE_ENOUGH_DATA) {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        if (!ctx) return;

        if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }

        if (currentTime - lastScanTime >= SCAN_DELAY) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

          qrWorker.current?.postMessage({
            imageData: imageData.data,
            width: imageData.width,
            height: imageData.height,
          });

          lastScanTime = currentTime;
        }
      }

      if (!cancelled) {
        requestId = requestAnimationFrame(tick);
      }
    };

    const startScanner = async (): Promise<void> => {
      try {
        stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
        const target = videoRef.current;
        if (target == null) return;
        target.srcObject = stream;
        requestAnimationFrame(tick);
      } catch (err) {
        console.error(`cannot access the camera: ${err}`);
      }
    };

    const stopScanner = () => {
      cancelAnimationFrame(requestId);

      const tracks = stream?.getTracks();

      if (!tracks) return;

      for (const track of tracks) {
        track.stop();
      }
    };

    const initialize = async () => {
      await startScanner();
      if (cancelled) stopScanner();
    };

    initialize();

    return () => {
      cancelled = true;
      stopScanner();
      qrWorker.current?.terminate();
    };
  }, []);
};
